// The per-message "how did it answer this" dialog body, driven by one Model
// Lab run: classification, timing, what it read, what it grounded on.

import { useState } from "react";

import { Badge } from "../ui";
import { titleCase } from "./shared";
import { fmtMs, stageLabel } from "./console_model";

export interface LabStage {
  stage: string;
  status?: string;
  latency_ms?: number;
  attributes?: Record<string, unknown>;
}

export interface LabSource {
  source_type?: string;
  title?: string;
  excerpt?: string;
  why_selected?: string;
  confidence?: number;
  review_status?: string;
  user_facing_eligible?: boolean;
}

export interface LabRetrieval {
  backend?: string;
  scores?: { n_matched?: number };
  verdict?: { band?: string; label?: string; Lambda?: number; A?: number; n_evidence?: number };
  risk_signals?: string[];
  chunks?: Array<{ content?: string; text?: string }>;
  playbook?: { mechanism?: string; steps?: string[] };
}

export interface LabRun {
  classification?: string;
  confidence?: number;
  graph_hit?: boolean;
  knowledge_hit?: boolean;
  commands?: Array<{ payload?: { flow_name?: string } }>;
  stages?: LabStage[];
  sources?: LabSource[];
  source_details?: LabSource[];
  retrieval?: LabRetrieval;
  latency_ms?: number;
  assistant_response?: string;
  metadata?: Record<string, unknown>;
  response?: { metadata?: Record<string, unknown> };
  emotion?: string;
  persona?: string;
  mirroring_strategy?: string;
  kg_band?: string;
}

const SIGNAL_LABELS: Record<string, string> = {
  asks_for_otp_or_pin: "Asks for OTP / PIN",
  qr_scan_to_receive_money: "QR scan to 'receive' money",
  urgency_pressure: "Urgency / pressure",
  authority_impersonation: "Impersonates an authority",
  payment_requested: "Payment requested",
  lookalike_domain: "Look-alike domain",
  shortened_link: "Shortened link",
  kyc: "KYC bait",
  collect_request: "UPI collect request",
  remote_control_app: "Remote-control app",
};
const humanSignal = (s: string) => SIGNAL_LABELS[s] ?? String(s || "").replace(/_/g, " ");

const SOURCE_TYPE_LABELS: Record<string, string> = {
  knowledge_chunk: "Knowledge base",
  graph_path: "Graph path",
  risk_signal: "Risk signal",
  url: "Web indicator",
  domain: "Web indicator",
  entity: "Entity",
};
const srcType = (t: string | undefined) => SOURCE_TYPE_LABELS[t ?? ""] ?? String(t || "Source").replace(/_/g, " ");

const isHashLike = (s: string) => /^[0-9a-f]{32,}$/i.test((s || "").trim());

function srcLabel(s: LabSource): string {
  const title = String(s.title ?? "").trim();
  if (title && !isHashLike(title)) return title.replace(/_/g, " ");
  const excerpt = String(s.excerpt ?? "").trim();
  if (!excerpt) return srcType(s.source_type);
  return excerpt.length > 70 ? `${excerpt.slice(0, 69)}…` : excerpt;
}

/** KG verdict bands from the SPRT vocabulary. */
const BAND_TONE: Record<string, "critical" | "warning" | "serious" | "neutral"> = {
  high_risk: "critical",
  caution: "warning",
  needs_review: "serious",
  low_signal: "neutral",
};

/** The per-message "how did it answer this" dialog, driven by one Model Lab run. */
export function TurnAnalysis({ data }: { data: LabRun }) {
  const [showTrace, setShowTrace] = useState(false);
  const stages = data.stages ?? [];
  const totalMs = Number(data.latency_ms ?? stages.reduce((a, s) => a + Number(s.latency_ms ?? 0), 0));
  const sources = data.source_details ?? data.sources ?? [];
  const retrieval = data.retrieval ?? {};
  const md = { ...(data.metadata ?? {}), ...(data.response?.metadata ?? {}) };
  const persona = {
    emotion: String(md.emotion ?? data.emotion ?? ""),
    persona: String(md.persona ?? data.persona ?? ""),
    mirroring: String(md.mirroring_strategy ?? data.mirroring_strategy ?? ""),
    kgBand: String(md.kg_band ?? data.kg_band ?? retrieval.verdict?.band ?? ""),
  };
  const hasPersona = Boolean(persona.emotion || persona.persona || persona.mirroring || persona.kgBand);
  const flowName = data.commands?.[0]?.payload?.flow_name;

  return (
    <>
      <div className="chips" style={{ marginBottom: "var(--sp-4)" }}>
        <Badge tone={data.classification && data.classification !== "no_scam" ? "critical" : "neutral"}>
          {String(data.classification || "no_scam").replace(/_/g, " ")}
        </Badge>
        <span className="muted">confidence {Math.round(Number(data.confidence ?? 0) * 100)}%</span>
        {data.graph_hit && <Badge tone="good">graph hit</Badge>}
        {data.knowledge_hit && <Badge tone="good">knowledge hit</Badge>}
        {flowName && <span className="muted">→ {flowName}</span>}
      </div>

      {hasPersona && (
        <div className="section">
          <h4>Emotion and persona</h4>
          <div className="chips">
            {persona.emotion && <Badge tone="info">Emotion: {titleCase(persona.emotion)}</Badge>}
            {persona.persona && <Badge tone="info">Persona: {titleCase(persona.persona)}</Badge>}
            {persona.mirroring && <Badge tone="neutral">Mirroring: {persona.mirroring.replace(/_/g, " ")}</Badge>}
            {persona.kgBand && (
              <Badge tone={BAND_TONE[persona.kgBand] ?? "neutral"}>KG band: {persona.kgBand.replace(/_/g, " ")}</Badge>
            )}
          </div>
        </div>
      )}

      <div className="section">
        <div className="row">
          <h4 style={{ flex: 1 }}>Time per step</h4>
          <span className="num strong">total {fmtMs(totalMs)}</span>
        </div>
        {stages.length === 0 && <p className="muted">No stage timing captured.</p>}
        {stages.map((s, i) => (
          <div key={`${s.stage}-${i}`} style={{ marginBottom: 6 }}>
            <div className="row" style={{ fontSize: 12 }}>
              <span>{stageLabel(s.stage)}</span>
              <span className="spacer" />
              <span className="num muted">{fmtMs(s.latency_ms)}</span>
            </div>
            <div className="meter">
              <span style={{ width: `${Math.max(2, Math.min(100, (Number(s.latency_ms ?? 0) / (totalMs || 1)) * 100))}%` }} />
            </div>
          </div>
        ))}
      </div>

      {retrieval.backend && (
        <div className="section">
          <h4>What it read from the knowledge graph</h4>
          <div className="row muted" style={{ fontSize: 12, flexWrap: "wrap" }}>
            <span>
              backend <span className="strong">{retrieval.backend}</span>
            </span>
            {retrieval.scores && (
              <span>
                matched <span className="strong">{retrieval.scores.n_matched ?? 0}</span> nodes
              </span>
            )}
          </div>
          {retrieval.verdict?.band && (
            <div className="card" style={{ marginTop: "var(--sp-2)" }}>
              <div className="strong">
                Graph verdict: {retrieval.verdict.band}
                {retrieval.verdict.label ? ` · ${retrieval.verdict.label}` : ""}
              </div>
              <div className="muted" style={{ fontSize: 11 }}>
                evidence score Λ {Number(retrieval.verdict.Lambda ?? 0).toFixed(2)} (accuse when ≥{" "}
                {Number(retrieval.verdict.A ?? 0).toFixed(1)}) · {retrieval.verdict.n_evidence ?? 0} pieces of evidence
              </div>
            </div>
          )}
          {!!retrieval.risk_signals?.length && (
            <>
              <div className="muted" style={{ fontSize: 12, marginTop: "var(--sp-2)" }}>
                Red flags found:
              </div>
              <div className="chips">
                {retrieval.risk_signals.map((sig, i) => (
                  <Badge tone="critical" key={`${sig}-${i}`}>
                    {humanSignal(sig)}
                  </Badge>
                ))}
              </div>
            </>
          )}
          {!!retrieval.chunks?.length && (
            <>
              <div className="muted" style={{ fontSize: 12, marginTop: "var(--sp-2)" }}>
                Knowledge it pulled:
              </div>
              {retrieval.chunks.slice(0, 5).map((c, i) => {
                const text = String(c.content ?? c.text ?? "").trim();
                return (
                  <p className="muted" key={i} style={{ fontSize: 11, marginBottom: 4 }}>
                    {text.length > 220 ? `${text.slice(0, 219)}…` : text}
                  </p>
                );
              })}
            </>
          )}
          {!!retrieval.playbook?.steps?.length && (
            <>
              <div className="muted" style={{ fontSize: 12, marginTop: "var(--sp-2)" }}>
                Recommended action
                {retrieval.playbook.mechanism ? ` (${String(retrieval.playbook.mechanism).replace(/_/g, " ")})` : ""}:
              </div>
              <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                {retrieval.playbook.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}

      <div className="section">
        <h4>Sources it grounded on ({sources.length})</h4>
        {sources.length === 0 && <p className="muted">No sources for this message.</p>}
        {sources.slice(0, 15).map((s, i) => (
          <div className="card" key={i} style={{ marginBottom: "var(--sp-2)" }}>
            <div className="row">
              <span className="strong" style={{ flex: 1 }}>
                {srcLabel(s)}
              </span>
              <Badge tone="neutral">{srcType(s.source_type)}</Badge>
            </div>
            {(s.excerpt || s.why_selected) && (
              <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
                {String(s.excerpt || s.why_selected).trim()}
              </p>
            )}
            <div className="muted" style={{ fontSize: 11 }}>
              {Math.round(Number(s.confidence ?? 0) * 100)}% match
              {s.review_status ? ` · ${s.review_status}` : ""}
              {s.user_facing_eligible ? " · shown to user" : ""}
            </div>
          </div>
        ))}
      </div>

      <div className="section">
        <button className="btn sm ghost" onClick={() => setShowTrace((v) => !v)}>
          {showTrace ? "▾" : "▸"} Full pipeline trace
        </button>
        {showTrace &&
          stages.map((s, i) => (
            <div className="card" key={i} style={{ marginTop: "var(--sp-2)" }}>
              <div className="row">
                <span className="strong" style={{ flex: 1 }}>
                  {stageLabel(s.stage)}
                </span>
                <span className="num muted">
                  {s.status} · {fmtMs(s.latency_ms)}
                </span>
              </div>
              {s.attributes && <pre className="pre-block">{JSON.stringify(s.attributes)}</pre>}
            </div>
          ))}
      </div>

      {data.assistant_response && (
        <div className="section">
          <h4>Response preview</h4>
          <p className="prewrap">{data.assistant_response}</p>
        </div>
      )}
    </>
  );
}
